import React, { useState } from "react";
import { Container } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import { Card, Col, Row, Button } from "react-bootstrap";
import CashIcon from "../../assets/images/cash-icon.png";
import BankIcon from "../../assets/images/bank-icon.png";
import CustomTable from "../../components/Common/CustomTable";
import { MODAL_SIZES } from "../../components/ConfirmModal";
import BankAccountModal from "./BankAccountModal";
import useApi from "../../Hooks/useApi";
import {
  deleteAccount,
  getBankAccountList,
  getTotalBalance,
  updateAccountStatus,
} from "../../helpers/API";
import { useNavigate } from "react-router-dom";
import { handleDelete, handleStatusChange } from "../../helpers/CommonAPICall";
import useFilter from "../../Hooks/useFilter";
import { formatINR } from "../../helpers";
import { DATE_FORMAT } from "../../helpers/dateFormat";

const Index = ({ t }) => {
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [filters, setFilters] = useFilter({});
  const [refresh, setRefresh] = useState(false);
  const navigate = useNavigate();
  const handleRefresh = () => setRefresh((prev) => !prev);
  const [accountData, setAccountData] = useState([
    {
      id: 1,
      title: t("account.list.buttons.CashBalance"),
      balance: "0",
      icon: CashIcon,
      altIcon: "Cash Icon",
    },
    {
      id: 2,
      title: t("account.list.buttons.BankBalance"),
      balance: "0",
      icon: BankIcon,
      altIcon: "Bank Icon",
    },
  ]);
  const [data, setData] = useApi({
    callback: getBankAccountList,
    payload: filters,
    dependencies: [filters, refresh],
  });
  useApi({
    callback: getTotalBalance,
    dependencies: [refresh],
    onSuccess: ({ bankBalance, cashBalance }) => {
      setAccountData([
        {
          id: 1,
          title: t("account.list.buttons.CashBalance"),
          balance: cashBalance?.[0]?.totalBalance || 0,
          icon: CashIcon,
          altIcon: "Cash Icon",
        },
        {
          id: 2,
          title: t("account.list.buttons.BankBalance"),
          balance: bankBalance?.[0]?.totalBalance || 0,
          icon: BankIcon,
          altIcon: "Bank Icon",
        },
      ]);
    },
  });

  const columns = [
    {
      name: t("account.list.columns.accountNo"),
      selector: (row) => row.accountNumber,
    },
    {
      name: t("account.list.columns.accountHolderName"),
      selector: (row) => row.accountHolderName,
    },
    {
      name: t("account.list.columns.bankName"),
      selector: (row) => row.bankName,
    },
    {
      name: t("account.list.columns.branch"),
      selector: (row) => row.branch,
    },
    {
      name: t("account.list.columns.ifscCode"),
      selector: (row) => row.ifsc,
    },
    {
      name: t("account.list.columns.balance"),
      selector: (row) => formatINR(row.balance),
      sortable: true,
      sortField: "balance",
    },
    {
      name: t("account.list.columns.status"),
      sortable: true,
      sortField: "isActive",
      isStatus: true,
      onClick: (data) => {
        handleStatusChange(
          data.id,
          { isActive: data.isActive },
          updateAccountStatus,
          setData
        );
      },
    },
    {
      name: t("account.list.columns.createdAt"),
      dateField: "createdAt",
      sortable: true,
      sortField: "createdAt",
      dateFormat: DATE_FORMAT.DISPLAY,
    },
    {
      name: t("account.list.columns.actions"),
      actionItems: [
        {
          label: t("account.list.actions.view"),
          icon: "ri-eye-fill",
          className: "btn btn-outline-primary",
          onClick: ({ id }) => {
            navigate(`/account/${id}`);
          },
        },
        {
          label: t("account.list.actions.edit"),
          icon: "ri-pencil-fill",
          className: "btn btn-outline-success",
          onClick: (data) => {
            setSelectedData(data);
            setShowAccountModal(true);
          },
        },
        {
          label: t("account.list.actions.delete"),
          icon: "ri-delete-bin-fill text-danger",
          className: "btn btn-outline-danger",
          isConfirm: true,
          variant: "DELETE",
          size: MODAL_SIZES.MEDIUM,
          onClick: (data) => {
            handleDelete(data.id, deleteAccount, setData, handleRefresh);
          },
        },
      ],
    },
  ];
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            navigation={true}
            title={t("breadcrumbs.capitalInvestment.list.title")}
            breadcrumbItem={t(
              "breadcrumbs.capitalInvestment.list.breadcrumbItem"
            )}
          />
          <Card>
            <Card.Body>
              <Row>
                {accountData.map((item, key) => (
                  <Col lg={5} key={key}>
                    <div className="d-flex gap-3">
                      <img
                        src={item.icon}
                        alt={item.altIcon}
                        style={{ width: "100px", height: "100px" }}
                      />
                      <div>
                        <span className="fs-3 fw-bold">{item.title}</span>
                        <span className="fs-3 fw-medium d-block">
                          {formatINR(item.balance)}
                        </span>
                      </div>
                    </div>
                  </Col>
                ))}
                <Col
                  lg={2}
                  className="d-flex align-items-center justify-content-center"
                >
                  <Button
                    className="d-block"
                    onClick={() => navigate("/account/cash-account")}
                  >
                    {t("account.list.buttons.cashBook")}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <CustomTable
            buttonLabel={t("account.list.buttons.addBank")}
            buttonAction={() => {
              setShowAccountModal(true);
            }}
            showSearch={false}
            columns={columns}
            data={data}
            searchKeys={["name", "email", "mobile"]}
            searchPlaceholder={t("subDistributor.list.search.placeholder")}
            onStatusChange={(data) => setFilters(data)}
            onRowClicked={(row) => {
              navigate(`/account/${row.id}`);
            }}
          />
          <BankAccountModal
            show={showAccountModal}
            toggle={() => setShowAccountModal(() => !showAccountModal)}
            data={selectedData}
            handleRefresh={handleRefresh}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(Index);
